import { useEffect, useMemo, useState } from "react";
import { Activity, CheckCircle2, HeartPulse, Trophy } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import MobileShell from "@/components/MobileShell";
import BottomNav from "@/components/BottomNav";
import api from "@/lib/api";

const isNumber = (value) => Number.isFinite(Number(value));

function shortDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recorded check-in";
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function MetricCard({ icon: Icon, label, value, detail }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        <Icon size={15} className="text-teal-600" aria-hidden="true" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      {detail && <p className="mt-1 text-xs text-slate-500">{detail}</p>}
    </div>
  );
}

export default function ProgressPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/dashboard/me")
      .then((response) => {
        if (active) setData(response.data || {});
      })
      .catch(() => {
        if (active) setData({});
      });
    return () => { active = false; };
  }, []);

  const painRecords = useMemo(() => (data?.pain_trend || [])
    .filter((record) => isNumber(record?.v))
    .map((record) => ({
      pain: Number(record.v),
      date: shortDate(record.t),
    })), [data]);

  if (!data) {
    return (
      <MobileShell>
        <main className="px-6 pt-8"><p className="text-sm text-slate-500">Loading your progress…</p></main>
        <BottomNav />
      </MobileShell>
    );
  }

  const activePlan = data.active_home_plan || data.active_program_plan;
  const completion = activePlan?.completion;
  const completionTotal = Number(completion?.total);
  const completionCompleted = Number(completion?.completed);
  const hasCompletion = Number.isFinite(completionTotal)
    && completionTotal > 0
    && Number.isFinite(completionCompleted)
    && completionCompleted >= 0;
  const completedExercises = hasCompletion ? Math.min(completionCompleted, completionTotal) : 0;
  const completionPercent = hasCompletion
    ? Math.max(0, Math.min(100, Number.isFinite(Number(completion?.percent))
      ? Number(completion.percent)
      : (completedExercises / completionTotal) * 100))
    : 0;
  const latestPain = painRecords[painRecords.length - 1];
  const sessionCount = Number(data.completed_sessions);
  const confidence = Number(data.confidence_avg);
  const badges = Array.isArray(data.badges) ? [...data.badges]
    .sort((a, b) => new Date(b.earned_at || 0) - new Date(a.earned_at || 0)) : [];
  const programName = data.condition?.name || data.condition?.program?.["Program Name"];
  const hasPathway = Boolean(data.condition);
  const currentWeek = Number(data.current_week_idx);
  const totalWeeks = Number(data.total_weeks);
  const summaryMetrics = [
    Number.isFinite(sessionCount) && sessionCount > 0 && { icon: CheckCircle2, label: "Sessions completed", value: sessionCount },
    data.completed_treatment_days > 0 && { icon: Activity, label: "Treatment days", value: data.completed_treatment_days, detail: "Sydney dates with exercise performed" },
    hasCompletion && { icon: Activity, label: "Earlier plan completion", value: `${completedExercises} of ${completionTotal}`, detail: "Lifetime exercise flags; dates unverified" },
    latestPain && { icon: HeartPulse, label: "Current pain", value: `${latestPain.pain}/10`, detail: latestPain.date },
    data.confidence_avg !== null && data.confidence_avg !== undefined && Number.isFinite(confidence) && { icon: Trophy, label: "Confidence", value: `${Math.round(confidence * 10) / 10}/10` },
  ].filter(Boolean);
  const hasProgressHistory = summaryMetrics.length > 0 || painRecords.length > 0 || badges.length > 0;

  return (
    <MobileShell>
      <main className="px-5 pb-28 pt-8 sm:px-6">
        <header className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Progress</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Progress</h1>
          <p className="mt-1 text-sm text-slate-500">See how you’re tracking with your recovery.</p>
        </header>
        {data.legacy_progress_count > 0 && <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">Earlier check-ins with unverified source are retained in history and excluded from these trends.</p>}

        {(hasPathway || activePlan) && (
          <section className="mb-4 rounded-2xl border border-teal-100 bg-teal-50/60 p-4" data-testid="current-program-status">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-teal-700">{hasPathway ? "Current Program" : "Current Exercise Program"}</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{hasPathway ? (programName || "Your Recovery Program") : (activePlan?.title || "Your Exercise Program")}</p>
            {hasPathway && data.week_progress_source !== "unverified" && Number.isFinite(currentWeek) && currentWeek >= 0 && (
              <p className="mt-1 text-sm text-slate-600">Week {currentWeek + 1}{Number.isFinite(totalWeeks) && totalWeeks > 0 ? ` of ${totalWeeks}` : ""}</p>
            )}
          </section>
        )}

        {summaryMetrics.length > 0 && (
          <section className="mb-5" aria-labelledby="summary-heading">
            <h2 id="summary-heading" className="mb-3 text-sm font-bold text-slate-900">Your summary</h2>
            <div className="grid grid-cols-2 gap-3">{summaryMetrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}</div>
          </section>
        )}

        {painRecords.length > 1 && (
          <section className="mb-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="pain-chart">
            <div className="flex items-baseline justify-between gap-3">
              <div><h2 className="font-semibold text-slate-900">Pain check-ins</h2><p className="mt-0.5 text-xs text-slate-500">Your recorded pain scores</p></div>
              <span className="text-sm font-semibold text-slate-700">Latest {latestPain.pain}/10</span>
            </div>
            <div className="mt-4 h-48" aria-label="Pain score over time">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={painRecords} margin={{ top: 6, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0, 10]} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip formatter={(value) => [`${value}/10`, "Pain"]} />
                  <Line type="monotone" dataKey="pain" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3, fill: "#0d9488" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {painRecords.length === 1 && (
          <section className="mb-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="latest-pain-score">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Latest pain check-in</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{latestPain.pain}/10</p>
            <p className="mt-1 text-sm text-slate-500">Recorded {latestPain.date}</p>
          </section>
        )}

        {hasCompletion && (
          <section className="mb-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="exercise-completion">
            <div className="flex items-start justify-between gap-4"><div><h2 className="font-semibold text-slate-900">Earlier plan completion</h2><p className="mt-1 text-sm text-slate-500">Lifetime exercise flags; treatment dates unverified</p></div><p className="shrink-0 text-sm font-semibold text-slate-900">{completedExercises} of {completionTotal}</p></div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100" aria-label={`${completedExercises} of ${completionTotal} exercises completed`}><div className="h-full rounded-full bg-teal-600" style={{ width: `${completionPercent}%` }} /></div>
          </section>
        )}

        {badges.length > 0 && (
          <section className="mb-5 rounded-2xl border border-slate-100 bg-slate-50 p-4" data-testid="milestones">
            <div className="flex items-center gap-2"><Trophy size={17} className="text-amber-500" aria-hidden="true" /><h2 className="font-semibold text-slate-800">Milestones</h2></div>
            <div className="mt-3 space-y-3">{badges.slice(0, 3).map((badge, index) => <div key={badge.id || badge.code || `${badge.title}-${index}`} className="rounded-xl bg-white px-3 py-2.5"><p className="text-sm font-semibold text-slate-800">{badge.title || "Achievement"}</p>{badge.description && <p className="mt-0.5 text-xs text-slate-500">{badge.description}</p>}</div>)}</div>
          </section>
        )}

        {!hasProgressHistory && (
          <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center" data-testid="progress-empty-state">
            <Activity size={24} className="mx-auto text-teal-600" aria-hidden="true" />
            <h2 className="mt-3 font-semibold text-slate-900">Your progress will appear here</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">Your progress will appear here as you complete exercises and check-ins.</p>
          </section>
        )}
      </main>
      <BottomNav />
    </MobileShell>
  );
}
